import { ArrowLeftIcon, CheckIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  copyTemplateByShareSlug,
  getTemplateAndBlocksByShareSlug
} from '../clients/templateClient';
import useNavigation from '../common/navigation';
import { ApiError } from '../common/types';
import ErrorBanner from '../components/ErrorBanner';
import LoadingState from '../components/LoadingState';
import MeetingTemplateBuilder from '../components/MeetingTemplateBuilder';
import NotFoundPage from './NotFoundPage';

export default function TemplatePreviewPage() {
  const { shareSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const navigation = useNavigation();

  // Fetch template data using React Query
  const {
    data: templateData,
    isLoading: isTemplateLoading,
    error: templateError
  } = useQuery({
    enabled: !!shareSlug,
    queryKey: ['getTemplateAndBlocksByShareSlug', shareSlug],
    queryFn: () => {
      if (!shareSlug) throw new Error('Share slug is required');
      return getTemplateAndBlocksByShareSlug(shareSlug);
    },
    retry: false
  });

  // Copy template mutation
  const copyTemplateMutation = useMutation({
    mutationFn: () => {
      if (!shareSlug) throw new Error('Share slug is required');
      return copyTemplateByShareSlug(shareSlug);
    },
    onSuccess: (copiedTemplate) => {
      // Navigate to the copied template after a brief success state
      setTimeout(() => {
        if (copiedTemplate.id) {
          navigation.goToTemplatePage(copiedTemplate.id, false);
        }
      }, 1000);
    }
  });

  // Auto-copy template if user just logged in with action=copy
  useEffect(() => {
    const shouldAutoCopy = searchParams.get('action') === 'copy';
    if (
      shouldAutoCopy &&
      user &&
      !authLoading &&
      !isTemplateLoading &&
      templateData &&
      !copyTemplateMutation.isPending &&
      !copyTemplateMutation.isSuccess
    ) {
      copyTemplateMutation.mutate();
      // Remove the action parameter from URL
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, [user, authLoading, isTemplateLoading, templateData, searchParams, copyTemplateMutation]);

  const handleCopyTemplate = async () => {
    if (!user) {
      // Redirect to login with redirect query parameter
      const redirectUrl = `/templates/preview/${shareSlug}?action=copy`;
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    copyTemplateMutation.mutate();
  };

  if (authLoading || isTemplateLoading) {
    return <LoadingState />;
  }

  // Handle template not found error
  if (templateError instanceof ApiError && templateError.status === 404) {
    return <NotFoundPage />;
  }

  // Show error if API call failed
  if (templateError && !(templateError instanceof ApiError && templateError.status === 404)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <ErrorBanner
            message={
              templateError instanceof Error
                ? templateError.message
                : 'Failed to load template. Please try again.'
            }
          />
        </div>
      </div>
    );
  }

  if (!templateData) {
    return <LoadingState />;
  }

  const { template, blocks } = templateData;
  const isCopying = copyTemplateMutation.isPending;
  const copySuccess = copyTemplateMutation.isSuccess;
  const copyError = copyTemplateMutation.error
    ? copyTemplateMutation.error instanceof Error
      ? copyTemplateMutation.error.message
      : 'Failed to save template. Please try again.'
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Public Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <button
                onClick={() => navigation.goToTemplatesPage()}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <img src="/logo_horizontal.png" alt="NoteWell" className="h-6 w-auto" />
              <span className="ml-3 text-sm text-gray-500">Template Preview</span>
            </div>

            {/* Auth Controls */}
            <div className="flex items-center gap-4">
              {user ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Signed in as {user.email}</span>
                  <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-medium">
                    {user.email?.[0].toUpperCase()}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Template Header with Copy Button */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{template.title}</h2>
              <p className="text-xs text-gray-500 mt-1">
                {blocks.length} question
                {blocks.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Copy Button */}
            <div className="flex-shrink-0">
              <button
                onClick={handleCopyTemplate}
                disabled={isCopying || copySuccess}
                className={`
                  flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white
                  transition-all duration-200 shadow-md hover:shadow-lg
                  ${
                    copySuccess
                      ? 'bg-green-500'
                      : isCopying
                        ? 'bg-indigo-400 cursor-wait'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                  }
                  disabled:cursor-not-allowed
                `}
              >
                {copySuccess ? (
                  <>
                    <CheckIcon className="h-5 w-5" />
                    <span>Saved!</span>
                  </>
                ) : isCopying ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-5 w-5" />
                    <span>Save to My Account</span>
                  </>
                )}
              </button>

              {copyError && <p className="text-sm text-red-600 mt-2 text-right">{copyError}</p>}
            </div>
          </div>

          {/* Info Banner for Non-Authenticated Users */}
          {!user && !authLoading && (
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-800">
                <span className="font-semibold">Want to use this template?</span> Sign in or create
                an account to save it to your workspace.
              </p>
            </div>
          )}
        </div>

        {/* Template Preview - Read-Only */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Template Preview</h3>
            <p className="text-sm text-gray-600 mt-1">
              This is a read-only preview. Save the template to edit and use it.
            </p>
          </div>

          <div className="p-4">
            <MeetingTemplateBuilder
              isNewTemplate={false}
              template={template}
              blocks={blocks}
              isReadOnly={true}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
